import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, Chip, CircularProgress, Paper, IconButton, Menu, MenuItem } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import TimelineIcon from '@mui/icons-material/Timeline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import LaunchIcon from '@mui/icons-material/Launch';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import config from '../config';
import DataTable from '../components/DataTable/DataTable';
import AssessmentModal from '../components/AssessmentModal';

const GroupDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [group, setGroup] = useState(null);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [headerStyles, setHeaderStyles] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [sortConfig, setSortConfig] = useState({});
    const [filters, setFilters] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [anchorElBulk, setAnchorElBulk] = useState(null);
    const [isGlobalSelection, setIsGlobalSelection] = useState(false);
    const [anchorElRow, setAnchorElRow] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    // For cleanup
    const abortControllerRef = useRef(null);
    const isMounted = useRef(true);

    const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);

    // Common function to calculate group table name
    const getGroupTableName = useCallback(() => {
        if (!group) return null;
        const normalizedGroupName = group.name
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');
        return `group_${normalizedGroupName}`;
    }, [group]);

    const handleRowActionClick = (event, row) => {
        setAnchorElRow(event.currentTarget);
        setSelectedRow(row);
    };

    const handleRowActionClose = () => {
        setAnchorElRow(null);
        // Do not clear selectedRow here
    };

    const handleBulkAddToPortfolio = async () => {
        setAnchorElBulk(null);
        if (selectedRows.length === 0 && !isGlobalSelection) return;

        setLoading(true);
        setError(null);

        try {
            const groupTableName = getGroupTableName();
            if (!groupTableName) return;

            if (isGlobalSelection) {
                const response = await axios.post(`${config.API_BASE_URL}/moodys/portfolio/bulk-add`, {
                    tableName: groupTableName,
                    filters: filters
                });
                const { summary } = response.data;
                setSuccessMessage(`Traitement global terminé : ${summary.success} ajoutés, ${summary.alreadyPresent} déjà présents, ${summary.failed} erreurs.`);
                const tableName = getGroupTableName();
                if (tableName) fetchGroupData(tableName);
            } else {
                let successCount = 0;
                let failCount = 0;
                let alreadyPresentCount = 0;

                for (const id of selectedRows) {
                    const row = data.find(r => r.id === id);
                    if (!row) continue;

                    try {
                        const name = row['original_name_partner'] || row['partners'] || row['Nom'] || row['nom'] || row['Name'] || row['name'] || row['Supplier Name'] || row['Raison Sociale'] || row['NOM'] || '';
                        const address = row['Adresse'] || row['adresse'] || row['Address'] || row['address'] || row['ADRESSE'] || row['supplier_address'] || row['adresse_fournisseur'] || row['road_or_avenue'] || '';
                        const city = row['Ville'] || row['ville'] || row['City'] || row['city'] || row['VILLE'] || row['city_name'] || '';
                        const country = row['organization_country'] || row['pays_d_intervention'] || row['localisation'] || row['Pays'] || row['pays'] || row['Country'] || row['country'] || row['PAYS'] || row['country_of_supplier_contact'] || 'France';

                        const response = await axios.post(`${config.API_BASE_URL}/moodys/portfolio/add`, {
                            name,
                            address,
                            city,
                            country,
                            tableName: groupTableName,
                            rowId: row.id
                        });

                        if (response.data.data.status === 'already_present') {
                            alreadyPresentCount++;
                        } else {
                            successCount++;
                        }
                    } catch (err) {
                        console.error(`Failed to add row ${id}:`, err);
                        failCount++;
                    }
                }
                setSuccessMessage(`Traitement groupé terminé : ${successCount} ajoutés, ${alreadyPresentCount} déjà présents, ${failCount} erreurs.`);
                const tableName = getGroupTableName();
                if (tableName) fetchGroupData(tableName);
            }
        } catch (err) {
            console.error("Bulk action error:", err);
            setError(err.response?.data?.error || "Erreur lors du traitement groupé.");
        } finally {
            setLoading(false);
            setSelectedRows([]);
            setIsGlobalSelection(false);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        fetchGroupDetails();
        return () => {
            isMounted.current = false;
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [id]);

    useEffect(() => {
        if (group) {
            const groupTableName = getGroupTableName();
            if (groupTableName) fetchGroupData(groupTableName);
        }
    }, [group, page, limit, sortConfig, filters, getGroupTableName]);


    const fetchGroupDetails = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/groups/${id}`);
            setGroup(response.data);
        } catch (err) {
            console.error(err);
            setError('Failed to load group details.');
            setLoading(false);
        }
    };

    const handleAddToPortfolio = async (row) => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const name = row['original_name_partner'] || row['partners'] || row['Nom'] || row['nom'] || row['Name'] || row['name'] || row['Supplier Name'] || row['Raison Sociale'] || row['NOM'] || '';
            const address = row['Adresse'] || row['adresse'] || row['Address'] || row['address'] || row['ADRESSE'] || row['supplier_address'] || row['adresse_fournisseur'] || row['road_or_avenue'] || '';
            const city = row['Ville'] || row['ville'] || row['City'] || row['city'] || row['VILLE'] || row['city_name'] || '';
            const country = row['organization_country'] || row['pays_d_intervention'] || row['localisation'] || row['Pays'] || row['pays'] || row['Country'] || row['country'] || row['PAYS'] || row['country_of_supplier_contact'] || 'France';

            const groupTableName = `group_${group.name
                .toLowerCase()
                .replace(/[^a-z0-9_]/g, '_')
                .replace(/_+/g, '_')
                .replace(/^_|_$/g, '')}`;

            const response = await axios.post(`${config.API_BASE_URL}/moodys/portfolio/add`, {
                name,
                address,
                city,
                country,
                tableName: groupTableName,
                rowId: row.id
            });

            const { data: result } = response.data;
            if (result.status === 'already_present') {
                setSuccessMessage(`Fournisseur "${name}" est déjà dans le portefeuille (ID: ${result.CF_PORTFOLIO_ID || 'N/A'})`);
            } else {
                setSuccessMessage(`Fournisseur "${name}" ajouté avec succès au portefeuille Moody's !`);
            }
            const tableName = getGroupTableName();
            if (tableName) fetchGroupData(tableName);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Erreur lors de l\'ajout au portefeuille Moody\'s.');
        } finally {
            setLoading(false);
        }
    };

    const handleAssessmentSubmit = async (model, options) => {
        setAssessmentModalOpen(false);
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        const rowsToProcess = [];
        if (selectedRow) {
            rowsToProcess.push(selectedRow);
        } else if (selectedRows.length > 0) {
            selectedRows.forEach(id => {
                const r = data.find(row => row.id === id);
                if (r) rowsToProcess.push(r);
            });
        }

        if (rowsToProcess.length === 0) {
            setLoading(false);
            setError("Aucune ligne sélectionnée pour l'assessment.");
            return;
        }

        const groupTableName = getGroupTableName();
        if (!groupTableName) {
            setLoading(false);
            setError("Impossible de déterminer le nom de la table.");
            return;
        }

        try {
            let successCount = 0;
            let failCount = 0;

            for (const rowData of rowsToProcess) {
                try {
                    const name = rowData['FOURNISSEUR'] || rowData['fournisseur'] || rowData['original_name_partner'] || rowData['partners'] || rowData['Nom'] || rowData['nom'] || rowData['Name'] || rowData['name'] || rowData['Supplier Name'] || rowData['Raison Sociale'] || rowData['NOM'] || '';
                    const address = rowData['ADRESSE'] || rowData['adresse'] || rowData['Adresse'] || rowData['Address'] || rowData['address'] || rowData['supplier_address'] || rowData['adresse_fournisseur'] || rowData['road_or_avenue'] || '';
                    const city = rowData['VILLE'] || rowData['ville'] || rowData['Ville'] || rowData['City'] || rowData['city'] || rowData['VILLE'] || rowData['city_name'] || '';
                    const country = rowData['organization_country'] || rowData['pays_d_intervention'] || rowData['localisation'] || rowData['Pays'] || rowData['pays'] || rowData['Country'] || rowData['country'] || rowData['PAYS'] || rowData['country_of_supplier_contact'] || 'France';
                    const bvdId = rowData['bvd_id'] || rowData['BvD_ID'] || rowData['BvDID'] || rowData['bvdid'];

                    await axios.post(`${config.API_BASE_URL}/moodys/assessment/start`, {
                        bvdId,
                        name,
                        address,
                        city,
                        country,
                        model,
                        options,
                        tableName: groupTableName,
                        rowId: rowData.id
                    });
                    successCount++;
                } catch (err) {
                    console.error(`Assessment failed for row ${rowData.id}:`, err);
                    failCount++;
                }
            }

            if (successCount > 0) {
                setSuccessMessage(`${successCount} assessment(s) démarré(s) avec succès !${failCount > 0 ? ` (${failCount} erreur(s))` : ''}`);
                fetchGroupData(groupTableName);
            } else if (failCount > 0) {
                setError(`Échec du lancement des assessments (${failCount} erreur(s)).`);
            }
        } catch (err) {
            console.error("Assessment error:", err);
            setError(err.response?.data?.error || "Erreur lors du lancement de l'assessment.");
        } finally {
            setLoading(false);
            setSelectedRow(null);
            setSelectedRows([]);
        }
    };

    const handleCompleteAssessment = async (row) => {
        if (!row) return;
        const cfRaId = row['cf_ra_id'] || row['CF_RA_Id'];

        if (!cfRaId) {
            setError("Impossible de finaliser : aucun ID d'assessment (CF_RA_Id) trouvé pour cette ligne.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${config.API_BASE_URL}/moodys/assessment/complete`, {
                cfRaId
            });

            if (response.data.success) {
                const status = response.data.data.CF_RA_Status || 'Completed';
                setSuccessMessage(`Assessment ${cfRaId} finalisé avec succès. Nouveau statut: ${status}`);

                const tableName = getGroupTableName();
                if (tableName) fetchGroupData(tableName); // Refresh the table
            }
        } catch (err) {
            console.error("Completion error:", err);
            setError(err.response?.data?.error || "Erreur lors de la finalisation de l'assessment.");
        } finally {
            setLoading(false);
            setSelectedRow(null);
        }
    }; const handleRefreshStatus = async (row) => {
        if (!row) return;
        const cfRaId = row['cf_ra_id'] || row['CF_RA_Id'];
        const id = row.id;

        // Determine group table name for update
        const groupTableName = getGroupTableName();
        if (!groupTableName) return;

        if (!cfRaId) {
            setError("Impossible de rafraîchir : aucun ID d'assessment (CF_RA_Id) trouvé pour cette ligne.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${config.API_BASE_URL}/moodys/assessment/refresh`, {
                cfRaId,
                tableName: groupTableName,
                id
            });

            if (response.data.success) {
                const status = response.data.data.CF_RA_Status || 'Updated';
                setSuccessMessage(`Statut rafraîchi avec succès. Nouveau statut: ${status}`);

                const tableName = getGroupTableName();
                if (tableName) fetchGroupData(tableName); // Refresh the table
            }
        } catch (err) {
            console.error("Refresh error:", err);
            setError(err.response?.data?.error || "Erreur lors du rafraîchissement du statut.");
        } finally {
            setLoading(false);
            setSelectedRow(null);
        }
    };


    const handleOpenInMoodys = (row) => {
        if (!row) return;
        const cfRaId = row['cf_ra_id'] || row['CF_RA_Id'];
        const cfPortfolioId = row['cf_portfolio_id'] || row['CF_PORTFOLIO_ID'];
        const bvdId = row['bvd_id'] || row['BvD_ID'] || row['BvDID'];

        const host = "https://compliancecatalyst2-r1.bvdinfo.com";

        let url = "";
        if (cfRaId) {
            // Direct PDF download using CF_RA_Id
            url = `${host}/compliancecatalyst4/CompaniesPortfolio/RiskAssessments/Guid?guid=${cfRaId}&export=pdf&restart=1`;
        } else if (cfPortfolioId) {
            // TODO: Portfolio page URL (to be implemented)
            setError("Téléchargement PDF disponible uniquement pour les assessments déjà lancés. Veuillez d'abord démarrer l'évaluation.");
            return;
        } else if (bvdId) {
            setError("Téléchargement PDF disponible uniquement pour les assessments déjà lancés. Veuillez d'abord démarrer l'évaluation.");
            return;
        }

        if (url) {
            window.open(url, '_blank');
        } else {
            setError("Identifiant d'assessment manquant (CF_RA_Id). Veuillez d'abord démarrer l'évaluation.");
        }
    };

    const handleOpenPortfolio = (row) => {
        if (!row) return;

        const bvdId = row.bvd_id ?? row.BvD_ID ?? row.BvDID;
        const cfPortfolioId = row.cf_portfolio_id ?? row.CF_PORTFOLIO_ID;

        console.log('--- handleOpenPortfolio ---');
        console.log('Row ID:', row.id, 'BvD_ID:', bvdId, 'CF_PORTFOLIO_ID:', cfPortfolioId);

        const host = "https://compliancecatalyst2-r1.bvdinfo.com";
        const basePath = "compliancecatalyst4";

        let url = "";
        if (bvdId) {
            url = `${host}/${basePath}/CompaniesPortfolio/Report?bvdid=${bvdId}&restart=1&sl=${Date.now()}`;
        } else if (cfPortfolioId) {
            url = `${host}/${basePath}/CompaniesPortfolio/Report?PortfolioId=${cfPortfolioId}&restart=1&sl=${Date.now()}`;
        }

        console.log('Final Portfolio URL:', url);

        if (url) {
            window.open(url, '_blank');
        } else {
            setError("Identifiant Moody's (BvD ID) manquant. Veuillez d'abord ajouter le fournisseur au portfolio.");
        }
    };

    const fetchGroupData = async (tableName) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);
        try {
            const params = {
                page,
                limit,
                sortColumn: sortConfig.key,
                sortDirection: sortConfig.direction,
                filters: JSON.stringify(filters)
            };
            const response = await axios.get(`${config.API_BASE_URL}/fournisseurs/${tableName}`, {
                params,
                signal: abortControllerRef.current.signal
            });

            if (!isMounted.current) return;

            setData(response.data.data);
            setTotal(response.data.total);

            // Enhance header styles with French labels for assessment columns
            const currentStyles = response.data.headerStyles || {};
            const enhancedStyles = {
                ...currentStyles,
                'cf_ra_id': { label: 'ID Assessment', color: null },
                'assessment_status': { label: 'Statut Assessment', color: null },
                'assessment_risk_score': { label: 'Score de Risque', color: null },
                'assessment_completion_date': { label: 'Date Complétion', color: null }
            };
            setHeaderStyles(enhancedStyles);

            if (response.data.data.length > 0) {
                const dataColumns = Object.keys(response.data.data[0]);
                // Filter out internal Moody's status/score columns that have cleaner virtual equivalents
                const filteredColumns = dataColumns.filter(c => ![
                    'risk_score',
                    'cf_ra_status',
                    'cf_ra_score',
                    'cf_ra_scorekey_cf_scoreandclasses_label'
                ].includes(c));

                // Add assessment columns before Actions
                const assessmentColumns = ['assessment_status', 'assessment_risk_score', 'assessment_completion_date'];

                // Ensure Actions is at the end
                const finalColumns = [...filteredColumns.filter(c => c !== 'Actions'), ...assessmentColumns, 'Actions'];
                setColumns(finalColumns);
            } else {
                setColumns(['Actions']);
                setHeaderStyles(enhancedStyles);
            }
        } catch (err) {
            if (axios.isCancel(err)) {
                console.log('Request cancelled:', err.message);
                return;
            }
            console.error('Error fetching group data:', err);
            const errorMsg = err.response?.data?.details || err.message;
            setError(`Failed to load group data: ${errorMsg}`);
            if (err.response?.status === 500) {
                alert(`Backend Error (500): ${errorMsg}`);
            }
        } finally {
            if (isMounted.current) setLoading(false);
        }
    };

    const renderScoreChip = (value) => {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        let color = '#e0e0e0';
        let label = 'UNKNOWN';
        let textColor = 'white';

        if (num <= 30) {
            color = '#ACD149'; // Green
            label = 'LOW';
        } else if (num <= 60) {
            color = '#FF9900'; // Orange
            label = 'MEDIUM';
        } else {
            color = '#CC0000'; // Red
            label = 'HIGH';
        }

        return (
            <Chip
                label={`${num} ${label}`}
                sx={{ backgroundColor: color, color: textColor, fontWeight: 'bold', height: 24 }}
            />
        );
    };

    const renderStatusChip = (value) => {
        if (!value || value === '-') return '-';

        let color = '#e0e0e0';
        let textColor = 'white';
        let label = value;

        const valLower = value.toLowerCase();
        if (valLower === 'in progress') {
            color = '#FFC000'; // Orange
        } else if (valLower === 'accepted') {
            color = '#00B050'; // Green
        } else if (valLower === 'started') {
            color = '#1976d2'; // Blue
        } else if (valLower === 'finalized' || valLower === 'completed') {
            color = '#4caf50'; // Standard green
        }

        return (
            <Chip
                label={label}
                size="small"
                sx={{
                    backgroundColor: color,
                    color: textColor,
                    fontWeight: 'bold',
                    minWidth: '100px',
                    fontSize: '0.75rem'
                }}
            />
        );
    };

    if (!group && loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/groupings')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4">Group: {group?.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            const params = new URLSearchParams();
                            if (filters && Object.keys(filters).length > 0) {
                                params.append('filters', JSON.stringify(filters));
                            }
                            const groupTableName = `group_${group.name
                                .toLowerCase()
                                .replace(/[^a-z0-9_]/g, '_')
                                .replace(/_+/g, '_')
                                .replace(/^_|_$/g, '')}`;
                            window.open(`${config.API_BASE_URL}/fournisseurs/${groupTableName}/export?${params.toString()}`, '_blank');
                        }}
                        disabled={!group}
                    >
                        Export Excel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={(e) => setAnchorElBulk(e.currentTarget)}
                        disabled={selectedRows.length === 0 && !isGlobalSelection}
                        startIcon={<AccountTreeIcon />}
                    >
                        Actions groupées ({isGlobalSelection ? total : selectedRows.length})
                    </Button>
                    <Menu
                        anchorEl={anchorElBulk}
                        open={Boolean(anchorElBulk)}
                        onClose={() => setAnchorElBulk(null)}
                    >
                        <MenuItem onClick={handleBulkAddToPortfolio}>
                            <AccountTreeIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Ajouter au Portfolio Moody's
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setAssessmentModalOpen(true); }}>
                            <PlayArrowIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Démarrer l'évaluation
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setSuccessMessage(`Recalculer les évaluations pour la sélection`); }}>
                            <RefreshIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Recalculer les évaluations en cours
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setSuccessMessage(`Supprimer les évaluations pour la sélection`); }}>
                            <DeleteIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Supprimer l'évaluation
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setSuccessMessage(`Modifier Monitoring Set pour la sélection`); }}>
                            <TimelineIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Modifier Monitoring Set
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setSuccessMessage(`Retirer du portfolio la sélection`); }}>
                            <DeleteOutlineIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Retirer du portfolio
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setSuccessMessage(`Ré-ajouter au portfolio la sélection`); }}>
                            <AddIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Ré-ajouter au portfolio
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setSuccessMessage(`Exporter la sélection`); }}>
                            <FileDownloadIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Exporter les entreprises
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorElBulk(null); setSuccessMessage(`Fermer les alertes pour la sélection`); }}>
                            <NotificationsOffIcon sx={{ mr: 1, fontSize: '1rem' }} />
                            Fermer les alertes
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

            {group && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                        Source: <strong>{group.table_name}</strong> | Created: {new Date(group.created_at).toLocaleString()}
                    </Typography>
                </Box>
            )}

            <DataTable
                data={data}
                columns={columns}
                headerStyles={headerStyles}
                total={total}
                page={page}
                limit={limit}
                onPageChange={(p) => setPage(p)}
                onLimitChange={(l) => { setLimit(l); setPage(1); }}
                onSort={(k, d) => setSortConfig({ key: k, direction: d })}
                onFilter={(f) => { setFilters(f); setPage(1); }}
                loading={loading}
                selectedRows={selectedRows}
                onSelectionChange={(newRows) => {
                    setSelectedRows(newRows);
                    if (newRows.length === 0) setIsGlobalSelection(false);
                }}
                isGlobalSelection={isGlobalSelection}
                onGlobalSelectionChange={setIsGlobalSelection}
                filterTypes={{
                    'assessment_status': 'select',
                    'assessment_risk_score': { type: 'select', options: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] },
                    'calcul_de_risque': { type: 'select', options: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] },
                    'Calcul de risque': { type: 'select', options: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] },
                    'score': { type: 'select', options: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] },
                    'Score': { type: 'select', options: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] }
                }}
                onFetchUniqueValues={async (column) => {
                    const normalizedGroupName = group.name
                        .toLowerCase()
                        .replace(/[^a-z0-9_]/g, '_')
                        .replace(/_+/g, '_')
                        .replace(/^_|_$/g, '');
                    const groupTableName = `group_${normalizedGroupName}`;
                    const response = await axios.get(`${config.API_BASE_URL}/fournisseurs/${groupTableName}/values/${column}`);
                    return response.data;
                }}
                customRenderers={{
                    'calcul_de_risque': renderScoreChip,
                    'Calcul de risque': renderScoreChip,
                    'score': renderScoreChip,
                    'Score': renderScoreChip,
                    'assessment_status': (value, row) => {
                        const status = row.cf_ra_status || '-';
                        return renderStatusChip(status);
                    },
                    'assessment_risk_score': (value, row) => {
                        const dbScore = row.cf_ra_score || row.cf_ra_scorekey_cf_scoreandclasses_label;
                        if (dbScore !== null && dbScore !== undefined && dbScore !== '') {
                            // If it looks like a label from the DB, convert to chip structure
                            if (typeof dbScore === 'string' && (dbScore.includes('HIGH') || dbScore.includes('MEDIUM') || dbScore.includes('LOW'))) {
                               return (
                                    <Chip
                                        label={dbScore}
                                        sx={{
                                            backgroundColor: dbScore.toUpperCase().includes('HIGH') ? '#CC0000' : (dbScore.toUpperCase().includes('MEDIUM') ? '#FF9900' : '#ACD149'),
                                            color: 'white',
                                            fontWeight: 'bold',
                                            height: 24
                                        }}
                                    />
                                );
                            }
                            return renderScoreChip(dbScore);
                        }
                        return '-';
                    },
                    'assessment_completion_date': (value, row) => {
                        const dateStr = row.cf_ra_date;
                        if (!dateStr) return '-';

                        try {
                            return new Date(dateStr).toLocaleDateString('fr-FR');
                        } catch (e) {
                            return dateStr;
                        }
                    },
                    'Actions': (value, row) => (
                        <IconButton size="small" onClick={(e) => handleRowActionClick(e, row)}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    )
                }}
            />

            <Menu
                anchorEl={anchorElRow}
                open={Boolean(anchorElRow)}
                onClose={handleRowActionClose}
            >
                <MenuItem onClick={() => { handleAddToPortfolio(selectedRow); handleRowActionClose(); }}>
                    <AccountTreeIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Ajouter au Portfolio
                </MenuItem>
                <MenuItem onClick={() => { handleRowActionClose(); setAssessmentModalOpen(true); }}>
                    <PlayArrowIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Démarrer l'évaluation
                </MenuItem>
                <MenuItem onClick={() => { handleCompleteAssessment(selectedRow); handleRowActionClose(); }}>
                    <CheckCircleIcon sx={{ mr: 1, fontSize: '1rem', color: '#4caf50' }} />
                    Finaliser l'évaluation
                </MenuItem>
                <MenuItem onClick={() => { handleOpenInMoodys(selectedRow); handleRowActionClose(); }}>
                    <LaunchIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Ouvrir le PDF Moody's
                </MenuItem>
                <MenuItem onClick={() => { handleOpenPortfolio(selectedRow); handleRowActionClose(); }}>
                    <FolderOpenIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Ouvrir le portfolio
                </MenuItem>
                <MenuItem onClick={() => { handleRefreshStatus(selectedRow); handleRowActionClose(); }}>
                    <RefreshIcon sx={{ mr: 1, fontSize: '1rem', color: '#1976d2' }} />
                    Rafraîchir le statut Moody's
                </MenuItem>
                <MenuItem onClick={() => { handleRowActionClose(); setSuccessMessage(`Supprimer l'évaluation pour ${selectedRow?.Nom || selectedRow?.Name}`); }}>
                    <DeleteIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Supprimer l'évaluation
                </MenuItem>
                <MenuItem onClick={() => { handleRowActionClose(); setSuccessMessage(`Modifier Monitoring Set pour ${selectedRow?.Nom || selectedRow?.Name}`); }}>
                    <TimelineIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Modifier Monitoring Set
                </MenuItem>
                <MenuItem onClick={() => { handleRowActionClose(); setSuccessMessage(`Retirer du portfolio : ${selectedRow?.Nom || selectedRow?.Name}`); }}>
                    <DeleteOutlineIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Retirer du portfolio
                </MenuItem>
                <MenuItem onClick={() => { handleRowActionClose(); setSuccessMessage(`Ré-ajouter au portfolio : ${selectedRow?.Nom || selectedRow?.Name}`); }}>
                    <AddIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Ré-ajouter au portfolio
                </MenuItem>
                <MenuItem onClick={() => { handleRowActionClose(); setSuccessMessage(`Exporter ${selectedRow?.Nom || selectedRow?.Name}`); }}>
                    <FileDownloadIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Exporter les entreprises
                </MenuItem>
                <MenuItem onClick={() => { handleRowActionClose(); setSuccessMessage(`Fermer les alertes pour ${selectedRow?.Nom || selectedRow?.Name}`); }}>
                    <NotificationsOffIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Fermer les alertes
                </MenuItem>
            </Menu>

            <AssessmentModal
                open={assessmentModalOpen}
                onClose={() => setAssessmentModalOpen(false)}
                onSubmit={handleAssessmentSubmit}
                companyName={selectedRow ? (selectedRow['original_name_partner'] || selectedRow['partners'] || selectedRow['Nom'] || selectedRow['Name']) : 'Sélection groupée'}
            />
        </Box>
    );
};

export default GroupDetail;
